import './docPreviewRow.scss';
import {
    useStatePreviewDocument,
    useStatePreviewDocumentWithHighlights,
    useStateQuery,
    useStateResultFor,
    useStateShowAnnotations
} from '../../../search-hooks';
import React, {useCallback, useEffect} from 'react';
import classNames from 'clsx';
import {PdfViewer} from '../../../../common/pdf-viewer/PdfViewer';
import {TextViewer} from '../../../../common/pdf-viewer/TextViewer';
import {useDispatch, useSelector} from 'react-redux';
import {ProdDocument, ProductDocument} from '../../../search-types';
import {PreviewDocument} from './docPreviewRow-types';
import {fetchHighlightedDocument} from './docPreviewRow-service';
import {useTranslation} from 'react-i18next';
import {getPreviewLink} from '../../../../services/search-service';
import {ImageViewer} from '../../../../common/image-viewer/ImageViewer';
import {SearchPageType} from '../../../search-config';
import {
    doFetchPdfDocumentPreviews,
    setIsLoadingDocumentAction,
    setPreviewDocumentAction
} from '../../../duck/search-actions';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'jfif', 'pjpeg', 'pjp', 'png', 'svg', 'gif'];

type DocPreviewRowProps = {
    selectedDocument: ProdDocument | undefined;
};

export const DocPreviewRow = ({selectedDocument}: DocPreviewRowProps): JSX.Element | null => {
    const dispatch = useDispatch();
    const searchPageType = SearchPageType.BASIC;
    const query = useStateQuery();
    const resultFor = useStateResultFor();
    const spotlightTagTypes = useSelector((state) => state.config.search.spotlightTagTypes);
    const settingsContainerName = useSelector((state) => state.settings.containerName);
    const {t} = useTranslation();
    const previewDocument = useStatePreviewDocument();
    const previewDocumentWithHighlights = useStatePreviewDocumentWithHighlights();
    const showAnnotations = useStateShowAnnotations();

    const fetchPdfDocumentPreviews = useCallback(async () => {
        dispatch(doFetchPdfDocumentPreviews(selectedDocument, showAnnotations, searchPageType, resultFor, query, settingsContainerName, spotlightTagTypes));
    }, [dispatch, query, resultFor, searchPageType, selectedDocument, settingsContainerName, showAnnotations, spotlightTagTypes]);

    const fetchAndSetPlainDocument = useCallback(async () => {
        if (selectedDocument) {
            if (query) {
                dispatch(setIsLoadingDocumentAction(true));
                const previewLink = selectedDocument.metadata.previewLink_string;
                const fetchDocumentPromise = await fetchHighlightedDocument(searchPageType, selectedDocument.id, resultFor || query, settingsContainerName, false, [], previewLink);
                dispatch(setPreviewDocumentAction(fetchDocumentPromise));
                dispatch(setIsLoadingDocumentAction(false));
            } else {
                dispatch(setPreviewDocumentAction({title: selectedDocument.title, content: selectedDocument.content}));
            }
        }
    }, [dispatch, query, resultFor, searchPageType, selectedDocument, settingsContainerName]);


    const isPdfFile = (previewPath: string) => {
        return !!previewPath && previewPath.toLowerCase().endsWith('.pdf');
    };

    useEffect(() => {
        if (selectedDocument !== undefined) {
            const previewPath = selectedDocument.metadata.previewLink_string;
            const isPdf = isPdfFile(previewPath);
            if (isPdf) {
                fetchPdfDocumentPreviews();
            } else {
                fetchAndSetPlainDocument();
            }
        }
    }, [fetchAndSetPlainDocument, fetchPdfDocumentPreviews, selectedDocument]);


    const isFileExtensionPartOfList = (selectedDocument: ProductDocument, extensionsList: string[]): boolean => {
        const fileExtension = selectedDocument.metadata?.previewLink_string?.split('.').pop();
        if (!fileExtension) {
            return false;
        }
        return extensionsList.includes(fileExtension?.toLowerCase());
    };

    const renderPdfViewer = useCallback((): JSX.Element => {
        return <PdfViewer
            file={showAnnotations ? previewDocumentWithHighlights?.documentBlob : previewDocument?.documentBlob}/>;
    }, [previewDocument, previewDocumentWithHighlights, showAnnotations]);

    const renderImageViewer = useCallback((selectedDocument: ProductDocument): JSX.Element => {
        const imageLink = getPreviewLink(searchPageType, selectedDocument.metadata.previewLink_string);
        return <ImageViewer imageLink={imageLink}/>;
    }, [searchPageType]);

    const renderTextViewer = useCallback((selectedDocument: ProductDocument, previewDocument: PreviewDocument): JSX.Element => {
        const titleHtml = previewDocument.title ? previewDocument.title : selectedDocument.title;
        let contentHtml: string;
        if (previewDocument.content) {
            contentHtml = previewDocument.content;
        } else {
            contentHtml = selectedDocument.content ? selectedDocument.content : t('search.document.unavailablePreview');
        }

        return <TextViewer title={titleHtml} content={contentHtml}/>;
    }, [t]);

    const renderViewer = useCallback((): JSX.Element | null => {
        if (selectedDocument) {
            let renderViewer;
            if (isFileExtensionPartOfList(selectedDocument, IMAGE_EXTENSIONS)) {
                renderViewer = renderImageViewer(selectedDocument);
            } else {
                if (isPdfFile(selectedDocument.metadata.previewLink_string) && (previewDocument?.documentBlob || previewDocumentWithHighlights?.documentBlob)) {
                    renderViewer = renderPdfViewer();
                } else if (previewDocument) {
                    renderViewer = renderTextViewer(selectedDocument, previewDocument);
                }
            }

            return (
                <div id="preview_content" className={classNames('details-body-base-data')}>
                    <div className="details-preview-doc">
                        {renderViewer}
                    </div>
                </div>
            );
        }
        return <></>;
    }, [selectedDocument, renderImageViewer, previewDocument, previewDocumentWithHighlights?.documentBlob, renderPdfViewer, renderTextViewer]);

    if (!selectedDocument || (!previewDocument && !previewDocumentWithHighlights)) {
        return <span id="preview_content_placeholder">{t('search.document.loading')}</span>;
    }

    return <>{renderViewer()}</>;
};
